import {
  Autocomplete,
  AutocompleteOption,
  createFilterOptions,
} from "@mui/joy";
import { useState } from "react";
import { FieldLabel } from "../formKit/formKit";

/** Sentinel id for the "+ New …" action row appended to every list. */
const ADD_NEW_ID = -999;

type WithIdName = { id: number; name?: string };

const filter = createFilterOptions<any>();

/**
 * A plain Autocomplete matching the Service form's row style, with a trailing
 * "+ New …" action that lets the user create the entity inline instead of
 * leaving the form. Selecting it calls `onCreateNew` (which opens the matching
 * create dialog); picking a real option calls `onSelect(id)`.
 *
 * @param {object} root0 props
 * @param {string} root0.label field label (e.g. "Venue")
 * @param {string} root0.icon FontAwesome icon class for the field tile
 * @param {string} root0.color tile color
 * @param {Array} root0.options selectable records (must have id + name)
 * @param {number} root0.valueId currently selected id
 * @param {Function} root0.onSelect called with the picked id (or undefined)
 * @param {Function} root0.onCreateNew called when "+ New …" is chosen
 * @param {string} root0.createLabel noun used in the "+ New …" row (defaults to label)
 * @returns {ReactElement} EntityPicker
 */
export function EntityPicker<T extends WithIdName>({
  label,
  icon,
  color,
  options,
  valueId,
  onSelect,
  onCreateNew,
  createLabel,
}: {
  label: string;
  icon: string;
  color: string;
  options: T[];
  valueId?: number;
  onSelect: (id?: number) => void;
  onCreateNew: () => void;
  createLabel?: string;
}) {
  // Bumped when "+ New …" is chosen so the Autocomplete remounts and drops the
  // sentinel label from its input; also remounts when the selection changes.
  const [remountKey, setRemountKey] = useState(0);
  const newLabel = `+ New ${createLabel ?? label}`;

  return (
    <Autocomplete
      key={`${valueId ?? "none"}-${remountKey}`}
      sx={{ background: "transparent" }}
      variant="plain"
      value={(options?.find((p) => p.id === valueId) ?? null) as any}
      startDecorator={<FieldLabel icon={icon} color={color} label={label} />}
      options={options}
      getOptionLabel={(option) => (option as T)?.name ?? ""}
      filterOptions={(opts, params) => {
        const filtered = filter(opts, params);
        // Always offer the create action, regardless of the search text.
        filtered.push({ id: ADD_NEW_ID, name: newLabel } as any);
        return filtered;
      }}
      renderOption={(optionProps, option) => {
        const { key, ...rest } = optionProps as any;
        const isAddNew = (option as any).id === ADD_NEW_ID;
        return (
          <AutocompleteOption
            key={isAddNew ? "add-new" : (option as T).id}
            {...rest}
            sx={
              isAddNew
                ? { color: "var(--joy-palette-primary-500)", fontWeight: 600 }
                : undefined
            }
          >
            {isAddNew ? (
              <>
                <i
                  className="fa-solid fa-plus"
                  style={{ marginRight: 8, fontSize: 12 }}
                ></i>
                New {createLabel ?? label}
              </>
            ) : (
              (option as T).name
            )}
          </AutocompleteOption>
        );
      }}
      slotProps={{
        input: { style: { textAlign: "right" } },
        listbox: { sx: { zIndex: 9999 } },
      }}
      onChange={(e, p) => {
        const picked = p as any;
        if (picked?.id === ADD_NEW_ID) {
          onCreateNew();
          // Clear the "+ New …" text the click left in the input.
          setRemountKey((k) => k + 1);
          return;
        }
        onSelect(picked?.id);
      }}
    />
  );
}
